<template>
    <div class="relative flex h-52 w-full items-center justify-center overflow-hidden bg-gray-200 lg:h-72 lg:w-1/2">
        <img
            :src="coverDisplaySrc"
            :alt="property.full_address"
            class="h-full w-full object-contain"
        />
        <button
            v-if="!property.deleted_at && !editingCover"
            type="button"
            class="absolute right-2 top-2 z-10 inline-flex items-center justify-center rounded-md bg-white/90 p-2 text-gray-800 shadow ring-1 ring-gray-200 hover:bg-white disabled:pointer-events-none disabled:opacity-50"
            :disabled="coverForm.processing || isSharedViewOnly"
            aria-label="Edit cover photo"
            @click="startCoverEdit"
        >
            <font-awesome-icon :icon="faPenToSquare" class="h-4 w-4" aria-hidden="true" />
        </button>
        <ToolTip
            v-if="isSharedViewOnly && viewOnlyTooltip"
            class="absolute right-2 top-14 z-10 bg-white/90 rounded-md p-0.5"
            :title="viewOnlyTooltip.title"
            :description="viewOnlyTooltip.description"
        />
        <div
            v-if="editingCover"
            class="absolute inset-0 z-20 flex items-center justify-center bg-slate-100 p-3 sm:p-4"
        >
            <div
                class="w-full max-w-md rounded-2xl border border-gray-200/80 bg-white p-4 shadow-sm ring-1 ring-black/5"
            >
                <h3 class="text-center text-sm font-semibold text-gray-900">
                    Update cover photo
                </h3>
                <p class="mt-1 text-center text-xs text-gray-500">
                    This image is shown on your listing and in search.
                </p>
                <input
                    ref="coverFileInput"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    class="hidden"
                    @change="onCoverFileChange"
                />
                <div class="mt-4">
                    <div
                        role="button"
                        tabindex="0"
                        :class="[
                            'group relative flex cursor-pointer flex-col items-center justify-center overflow-hidden rounded-xl border-2 border-dashed px-3 py-5 transition',
                            coverDragActive
                                ? 'border-rent-risk-blue-400 bg-rent-risk-blue-50/80'
                                : 'border-gray-300 bg-gray-50/90 hover:border-gray-400',
                        ]"
                        @click="triggerCoverFile"
                        @keydown.enter="triggerCoverFile"
                        @keydown.space.prevent="triggerCoverFile"
                        @dragover.prevent="coverDragActive = true"
                        @dragleave="onCoverDragLeave"
                        @drop.prevent="onCoverDrop"
                    >
                        <div
                            v-if="coverPreviewUrl"
                            class="mb-2 h-20 w-full overflow-hidden rounded-lg bg-gray-200 ring-1 ring-gray-200/80"
                        >
                            <img
                                :src="coverPreviewUrl"
                                alt="Selected cover preview"
                                class="h-full w-full object-cover"
                            />
                        </div>
                        <div
                            v-else
                            class="mb-0 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-gray-200/80"
                        >
                            <font-awesome-icon
                                :icon="faCloudArrowUp"
                                class="h-5 w-5 text-rent-risk-blue-400"
                                aria-hidden="true"
                            />
                        </div>
                        <p class="mt-1 text-center text-sm font-medium text-gray-800">
                            <span v-if="!coverForm.coverPhoto">Drop an image or </span>
                            <span
                                v-if="!coverForm.coverPhoto"
                                class="text-rent-risk-blue-500 underline decoration-rent-risk-blue-500/30 underline-offset-2 group-hover:text-rent-risk-blue-600"
                            >
                                browse
                            </span>
                            <span v-else class="text-gray-900">Click or drop to replace</span>
                        </p>
                        <p class="mt-1 text-center text-xs text-gray-500">
                            JPG, PNG, or WebP — up to 5&nbsp;MB
                        </p>
                        <p
                            v-if="coverForm.coverPhoto"
                            class="mt-2 max-w-full truncate text-center text-xs text-gray-600"
                        >
                            {{ coverForm.coverPhoto.name }}
                        </p>
                    </div>
                </div>
                <p v-if="coverClientError || coverForm.errors.coverPhoto" class="mt-2 text-center text-xs text-red-600">
                    {{ coverClientError || coverForm.errors.coverPhoto }}
                </p>
                <div class="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2">
                    <button
                        type="button"
                        class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 sm:w-auto"
                        @click="cancelCoverEdit"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="w-full rounded-lg bg-rent-risk-blue-400 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                        :disabled="!coverForm.coverPhoto || coverForm.processing"
                        @click="saveCover"
                    >
                        <span v-if="coverForm.processing">Saving…</span>
                        <span v-else>Save cover photo</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { appUrl } from '@/appUrl'
import { computed, ref, watch } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCloudArrowUp, faPenToSquare } from '@fortawesome/pro-solid-svg-icons'
import ToolTip from '@/Shared/ToolTip.vue'

const route =
    (typeof window !== 'undefined' && window.route) ||
    ((..._args) => '#')

const props = defineProps({
    property: {
        type: Object,
        required: true,
    },
    propertyAccess: {
        type: String,
        default: 'owner',
    },
    viewOnlyTooltip: {
        type: Object,
        default: null,
    },
})

const isSharedViewOnly = computed(() => props.propertyAccess === 'shared_view')

const coverPreviewUrl = ref(null)
const coverFileInput = ref(null)
const coverDragActive = ref(false)
const coverClientError = ref('')
const editingCover = ref(false)

const coverForm = useForm({ coverPhoto: null })

const propertyImage = computed(() => {
    return props.property.photo
        ? props.property.photo.startsWith('http')
            ? props.property.photo
            : appUrl(`/storage/${props.property.photo}`)
        : '/default-property-image.jpg'
})

const coverDisplaySrc = computed(() => {
    if (coverPreviewUrl.value) {
        return coverPreviewUrl.value
    }
    return propertyImage.value
})

watch(
    () => props.property.photo,
    () => {
        if (coverPreviewUrl.value) {
            URL.revokeObjectURL(coverPreviewUrl.value)
            coverPreviewUrl.value = null
        }
    },
)

const coverMaxBytes = 5 * 1024 * 1024

const isAcceptableCoverFile = (f) => {
    const nameOk = /\.(jpe?g|png|webp)$/i.test(f.name)
    const type = (f.type || '').toLowerCase()
    const typeOk = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(type) || (type === '' && nameOk)

    return typeOk
}

const applyCoverFile = (f) => {
    if (!f) {
        if (coverPreviewUrl.value) {
            URL.revokeObjectURL(coverPreviewUrl.value)
            coverPreviewUrl.value = null
        }
        coverForm.clearErrors()
        coverForm.coverPhoto = null
        coverClientError.value = ''

        return
    }
    if (f.size > coverMaxBytes) {
        coverClientError.value = 'That file is over 5 MB. Please choose a smaller image.'

        return
    }
    if (!isAcceptableCoverFile(f)) {
        coverClientError.value = 'Please use a JPG, PNG, or WebP file.'

        return
    }
    if (coverPreviewUrl.value) {
        URL.revokeObjectURL(coverPreviewUrl.value)
    }
    coverClientError.value = ''
    coverForm.clearErrors()
    coverForm.coverPhoto = f
    coverPreviewUrl.value = URL.createObjectURL(f)
}

const triggerCoverFile = () => {
    coverFileInput.value?.click()
}

const onCoverDragLeave = (e) => {
    const el = e.currentTarget
    if (el && e.relatedTarget instanceof Node && el.contains(e.relatedTarget)) {
        return
    }
    coverDragActive.value = false
}

const onCoverDrop = (e) => {
    coverDragActive.value = false
    const f = e.dataTransfer?.files?.[0]
    if (!f) {
        return
    }
    applyCoverFile(f)
    if (coverFileInput.value) {
        coverFileInput.value.value = ''
    }
}

const startCoverEdit = () => {
    if (isSharedViewOnly.value) {
        return
    }
    coverClientError.value = ''
    editingCover.value = true
    coverForm.clearErrors()
    coverForm.coverPhoto = null
}

const cancelCoverEdit = () => {
    coverClientError.value = ''
    if (coverPreviewUrl.value) {
        URL.revokeObjectURL(coverPreviewUrl.value)
        coverPreviewUrl.value = null
    }
    coverForm.clearErrors()
    coverForm.coverPhoto = null
    coverDragActive.value = false
    editingCover.value = false
    if (coverFileInput.value) {
        coverFileInput.value.value = ''
    }
}

const onCoverFileChange = (e) => {
    const f = e.target?.files?.[0]
    applyCoverFile(f || null)
}

const saveCover = () => {
    if (!coverForm.coverPhoto) {
        return
    }
    coverForm.post(route('properties.update-cover-photo', props.property.id), {
        forceFormData: true,
        preserveScroll: true,
        only: ['property'],
        onSuccess: () => {
            if (coverPreviewUrl.value) {
                URL.revokeObjectURL(coverPreviewUrl.value)
                coverPreviewUrl.value = null
            }
            coverForm.coverPhoto = null
            editingCover.value = false
            if (coverFileInput.value) {
                coverFileInput.value.value = ''
            }
        },
    })
}
</script>
